import {
	forwardRef,
	PropsWithChildren,
	ReactElement,
	Ref,
	RefAttributes,
	RefObject,
	useCallback,
	useImperativeHandle,
	useMemo
} from "react";
import { SelectedComboBoxItem } from "./ComboBox";
import { DropOver } from "../DropOver/DropOver";
import { ActionListBox } from "../ActionListBox/ActionListBox";
import {
	convertArbitraryValueForTrackingId,
	generateTrackingId,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { BusyIndicator } from "../BusyIndicator/BusyIndicator";
import { NoResults } from "../NoResults/NoResults";
import { localize } from "client_legacy/ndaw/culture";
import { ActionList } from "../ActionList/ActionList";
import {
	KeyBoardListNavigationContext,
	KeyBoardListNavigationContextData,
	useKeyboardListNavigationItem
} from "client/Hooks";
import { ActionListItem } from "../ActionList/ActionListItem";
import { Highlight } from "../HighlightedText/HighlightedText";
import styles from "./SuggestionBox.module.less";
import { Key } from "client/Misc/commonTypes";

export type SuggestedComboBoxItem<TValue, TRenderData = undefined> =
	& SelectedComboBoxItem<TValue>
	& {
		valueTrackingId?: string;
		highlight?: Highlight;
	}
	& ([TRenderData] extends [undefined]
		? {
			data?: TRenderData;
		}
		: {
			data: TRenderData;
		}
	);

type EmptySearchTermBehavior = "search" | "show-hint";

export type BaseSuggestionBoxProps<TValue,> = {
	target: HTMLElement;
	targetWidth: number;
	searchTerm: string;
	onSelectItem: (suggestion: SelectedComboBoxItem<TValue>) => void;
	onClose: () => void;
	trackingId: string;
	keyboardNavigationContext: KeyBoardListNavigationContextData;
};

export type ComboBoxSuggestionBoxProps<TValue, TRenderData> = {
	baseProps: BaseSuggestionBoxProps<TValue>;
	isLoading?: boolean;
	suggestedItems: readonly SuggestedComboBoxItem<TValue, TRenderData>[];
	emptySearchTermBehavior?: EmptySearchTermBehavior;
	menuTitle: string;
	children?: (
		suggestion: SuggestedComboBoxItem<TValue, TRenderData>,
		execute: () => void
	) => ReactElement;
};

export type ComboBoxSuggestionBoxHandle<TValue,> = {
	getTopSuggestion: () => SelectedComboBoxItem<TValue> | undefined;
};

const SuggestionBoxInternal = forwardRef(
	<TValue, TRenderData>(
		props: ComboBoxSuggestionBoxProps<TValue, TRenderData>,
		ref: Ref<ComboBoxSuggestionBoxHandle<TValue>>
	): ReactElement => {
		const {
			baseProps: {
				target,
				targetWidth,
				onSelectItem,
				onClose,
				searchTerm,
				keyboardNavigationContext,
				trackingId
			},
			menuTitle,
			suggestedItems,
			emptySearchTermBehavior,
			isLoading,
			children
		} = props;

		useImperativeHandle(
			ref,
			(): ComboBoxSuggestionBoxHandle<TValue> => ({
				getTopSuggestion: (): SelectedComboBoxItem<TValue> | undefined => {
					if (isLoading) {
						return undefined;
					}

					return suggestedItems.length > 0
						? suggestedItems[0]
						: undefined;
				}
			}),
			[isLoading, suggestedItems]
		);

		const renderContent = (): ReactElement => {
			if (isLoading) {
				return <LoadingScreen />;
			}

			if (suggestedItems.length === 0) {
				return (
					<NoResultsScreen
						searchTerm={searchTerm}
						emptySearchTermBehavior={emptySearchTermBehavior}
					/>
				);
			}

			return (
				<List
					suggestedItems={suggestedItems}
					onSelectItem={onSelectItem}
					keyboardNavigationContext={keyboardNavigationContext}
				>
					{children}
				</List>
			);
		};

		return (
			<Container
				target={target}
				targetWidth={targetWidth}
				menuTitle={menuTitle}
				onClose={onClose}
				trackingId={trackingId}
			>
				{renderContent()}
			</Container>
		);
	}
);
SuggestionBoxInternal.displayName = "ComboBox.SuggestionBox";

export const SuggestionBox = SuggestionBoxInternal as unknown as {
	<TValue, TRenderData>(props: ComboBoxSuggestionBoxProps<TValue, TRenderData> & RefAttributes<ComboBoxSuggestionBoxHandle<TValue>>): ReactElement;
	displayName: string;
	Container: typeof Container;
	LoadingScreen: typeof LoadingScreen;
	NoResultsScreen: typeof NoResultsScreen;
	List: typeof List;
};

type ContainerProps = {
	target: HTMLElement;
	targetWidth: number;
	menuTitle: string;
	onClose: () => void;
	trackingId: string;
};

const Container = (props: PropsWithChildren<ContainerProps>): ReactElement => {
	const {
		target,
		targetWidth,
		menuTitle,
		onClose,
		trackingId,
		children
	} = props;

	const outsideClickIgnoreRefs = useMemo(
		() => {
			const targetRef: RefObject<HTMLElement> = {
				current: target
			};

			return [targetRef];
		},
		[target]
	);

	return (
		<DropOver
			target={target}
			onClose={onClose}
			outsideClickIgnoreRefs={outsideClickIgnoreRefs}
			hasResponsiveDropOver={false}
			closeOnMouseDown
		>
			<ActionListBox
				isHeightConstrainted
				responsiveMode={{ kind: "never"}}
				width={targetWidth}
				menuTitle={menuTitle}
				trackingId={trackingId}
			>
				{children}
			</ActionListBox>
		</DropOver>
	);
};
Container.displayName = "ComboBox.SuggestionBox.Container";

const LoadingScreen = (): ReactElement => {

	return (
		<div className={styles.busyIndicatorContainer}>
			<BusyIndicator />
		</div>
	);
};
LoadingScreen.displayName = "ComboBox.SuggestionBox.LoadingScreen";

type NoResultsScreenProps = {
	searchTerm: string;
	emptySearchTermBehavior?: EmptySearchTermBehavior;
};

const NoResultsScreen = (props: NoResultsScreenProps): ReactElement => {
	const {
		searchTerm,
		emptySearchTermBehavior = "search"
	} = props;

	const shouldShowEmptySearchHint: boolean = searchTerm === ""
		&& emptySearchTermBehavior === "show-hint";

	return (
		<NoResults
			icon="magnifying-glass"
			title={localize(
				shouldShowEmptySearchHint
					? "_EnterASearchTerm"
					: "_NoSearchResults"
			)}
			message={localize(
				shouldShowEmptySearchHint
					? "_PleaseEnterASearchTermToBeginYourSearch"
					: "_PleaseTrySearchingWithAnotherTerm"
			)}
			height="fill"
			trackingId="no-results"
		/>
	);
};
NoResultsScreen.displayName = "ComboBox.SuggestionBox.NoResultsScreen";

type ListProps<TValue, TRenderData> = {
	suggestedItems: readonly SuggestedComboBoxItem<TValue, TRenderData>[];
	onSelectItem: (suggestion: SelectedComboBoxItem<TValue>) => void;
	keyboardNavigationContext: KeyBoardListNavigationContextData;
	children?: (
		suggestion: SuggestedComboBoxItem<TValue, TRenderData>,
		execute: () => void
	) => ReactElement;
};

const List = <TValue, TRenderData>(props: ListProps<TValue, TRenderData>): ReactElement => {
	const {
		suggestedItems,
		onSelectItem,
		keyboardNavigationContext,
		children
	} = props;

	if (children) {
		return (
			<CustomList
				suggestedItems={suggestedItems}
				onSelectItem={onSelectItem}
				keyboardNavigationContext={keyboardNavigationContext}
				renderItem={children}
			/>
		);
	}

	return (
		<KeyBoardListNavigationContext.Provider value={keyboardNavigationContext}>
			<ActionList
				width="fill"
				trackingId="suggestions"
			>
				{suggestedItems.map((item, index) => (
					<ListItem
						key={getItemKey(item, index)}
						index={index}
						item={item}
						onSelect={onSelectItem}
					/>
				))}
			</ActionList>
		</KeyBoardListNavigationContext.Provider>
	);
};
List.displayName = "ComboBox.SuggestionBox.List";

SuggestionBox.Container = Container;
SuggestionBox.LoadingScreen = LoadingScreen;
SuggestionBox.NoResultsScreen = NoResultsScreen;
SuggestionBox.List = List;

type ListItemProps<TValue, TRenderData> = {
	index: number;
	item: SuggestedComboBoxItem<TValue, TRenderData>;
	onSelect: (value: SelectedComboBoxItem<TValue>) => void;
};

const ListItem = <TValue, TRenderData>(props: ListItemProps<TValue, TRenderData>): ReactElement => {
	const {
		index,
		item,
		onSelect
	} = props;

	const {
		onKeyDown,
		ref
	} = useKeyboardListNavigationItem({
		itemIndex: index,
		onExecute: () => onSelect(item),
		executeKey: [
			Key.Enter,
			Key.Space
		],
		shouldCancelOnTab: true
	});

	return (
		<ActionListItem
			ref={ref}
			onKeyDown={onKeyDown}
			text={item.text}
			highlight={item.highlight}
			onClick={() => onSelect(item)}
			trackingId={generateTrackingId("value", `|${getItemValueTrackingId(item)}`)}
			shouldNotAutoCloseContainingDropOver
			tabIndex={-1}
		/>
	);
};
ListItem.displayName = "ListItem";

type CustomListProps<TValue, TRenderData> = {
	suggestedItems: readonly SuggestedComboBoxItem<TValue, TRenderData>[];
	keyboardNavigationContext: KeyBoardListNavigationContextData;
	onSelectItem: (suggestion: SelectedComboBoxItem<TValue>) => void;
	renderItem: (
		suggestion: SuggestedComboBoxItem<TValue, TRenderData>,
		execute: () => void
	) => ReactElement;
};

const CustomList = <TValue, TRenderData>(props: CustomListProps<TValue, TRenderData>): ReactElement => {
	const {
		keyboardNavigationContext,
		suggestedItems,
		onSelectItem,
		renderItem
	} = props;

	return (
		<KeyBoardListNavigationContext.Provider value={keyboardNavigationContext}>
			<ul className={styles.customList}>
				{suggestedItems.map(
					(x, i) => (
						<CustomListItem
							index={i}
							item={x}
							renderItem={renderItem}
							onSelect={onSelectItem}
							key={getItemKey(x, i)}
						/>
					)
				)}
			</ul>
		</KeyBoardListNavigationContext.Provider>
	);
};
CustomList.displayName = "CustomList";

type CustomListItemProps<TValue, TRenderData> = {
	index: number;
	item: SuggestedComboBoxItem<TValue, TRenderData>;
	onSelect: (value: SelectedComboBoxItem<TValue>) => void;
	renderItem: (
		suggestion: SuggestedComboBoxItem<TValue, TRenderData>,
		execute: () => void
	) => ReactElement;
};

const CustomListItem = <TValue, TRenderData>(props: CustomListItemProps<TValue, TRenderData>): ReactElement => {
	const {
		index,
		item,
		onSelect,
		renderItem
	} = props;

	const executeHandler = useCallback(
		(): void => {
			onSelect(item);
		},
		[item, onSelect]
	);

	const {
		onKeyDown,
		ref
	} = useKeyboardListNavigationItem({
		itemIndex: index,
		onExecute: executeHandler,
		executeKey: [
			Key.Enter,
			Key.Space
		],
		shouldCancelOnTab: true
	});

	return (
		<li
			className={styles.customListItem}
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
		>
			{renderItem(item, executeHandler)}
		</li>
	);
};
CustomListItem.displayName = "CustomListItem";

function getItemValueTrackingId<TValue, TRenderData>(item: SuggestedComboBoxItem<TValue, TRenderData>): string {
	if (isNotNilOrEmpty(item.valueTrackingId)) {
		return item.valueTrackingId;
	}

	return convertArbitraryValueForTrackingId(item.value);
}

function getItemKey<TValue, TRenderData>(item: SuggestedComboBoxItem<TValue, TRenderData>, index: number): string {
	if (isNotNilOrEmpty(item.valueTrackingId)) {
		return `tid-${item.valueTrackingId}`;
	}

	if (["number", "string"].includes(typeof item.value)) {
		return `value-${item.value}`;
	}

	return `index-${index}`;
};